import { TFunction } from 'i18next'
import { PCL_CARD_EXPIRY_LIMIT_IN_MONTHS } from './credit-card'
import { OrderChannel } from '@afterpaytouch/portal-api/consumer/ordertransactions/types'
import type { LoggedInLoadingMessageKey } from '../state'
import { CardBrand } from '@afterpay/types'
import { AssetId, CardErrorReason, ExperimentValue, PostCheckoutFlow } from '../model'

type LoadingMessages = {
  title: string
  description: string
}

type TranslateFn = (key: string) => string
type ProcessingTextType = 'heading' | 'description' | 'instruction'
type ProcessingVariantFlow = `${ExperimentValue}-${PostCheckoutFlow}`

interface ProcessingAssetTranslationConfig {
  flowVariantResolvers?: Partial<Record<ProcessingVariantFlow, ProcessingAssetTranslationResolver>>
  variantResolvers?: Partial<Record<ExperimentValue, ProcessingAssetTranslationResolver>>
  baseResolver?: ProcessingAssetTranslationResolver
}

type ProcessingAssetTranslationMap = Partial<Record<ProcessingTextType, ProcessingAssetTranslationConfig>>
type ProcessingAssetTranslationResolver = (t: TFunction, options?: ProcessingAssetTranslationOptions) => string

export const getChannelTranslation = (channel: OrderChannel, t: TFunction): string => {
  switch (channel) {
    case OrderChannel.ONLINE:
      return t('common:channel.online')
    case OrderChannel.IN_STORE:
      return t('common:channel.inStore')
    case OrderChannel.IN_STORE_CARD:
      return t('common:channel.inStoreCard')
    case OrderChannel.IN_STORE_CARD_ONLINE:
      return t('common:channel.inStoreCardOnline')
    case OrderChannel.MONEY:
      return t('common:channel.money')
  }
}

export interface ProcessingAssetTranslationOptions {
  [key: string]: unknown
  defaultValue?: string
  merchantName?: string
}

const PROCESSING_ASSET_TRANSLATION_MAP: Partial<Record<AssetId, ProcessingAssetTranslationMap>> = {
  [AssetId.Survey202303]: {
    heading: {
      variantResolvers: {
        B: (t, options) =>
          t('processing:asset:ap-asset-202303.heading__B', {
            ...options,
            defaultValue: t('processing:asset:ap-asset-202303.heading', options),
          }),
      },
      baseResolver: (t, options) => t('processing:asset:ap-asset-202303.heading', options),
    },
    description: {
      baseResolver: (t, options) => t('processing:asset:ap-asset-202303.description', options),
    },
  },
  [AssetId.GiftCard202306]: {
    heading: {
      baseResolver: (t, options) => t('processing:asset:ap-gift-card-202306.heading', options),
    },
    description: {
      baseResolver: (t, options) => t('processing:asset:ap-gift-card-202306.description', options),
    },
  },
  [AssetId.MarketingOptIn202308]: {
    heading: {
      baseResolver: (t, options) => t('processing:asset:ap-marketing-opt-in-202308.heading', options),
    },
    description: {
      baseResolver: (t, options) => t('processing:asset:ap-marketing-opt-in-202308.description', options),
    },
    instruction: {
      baseResolver: (t, options) => t('processing:asset:ap-marketing-opt-in-202308.instruction', options),
    },
  },
  [AssetId.GoogleSSO]: {
    heading: {
      baseResolver: (t, options) => t('processing:asset:google-sso-202308.heading', options),
    },
    description: {
      baseResolver: (t, options) => t('processing:asset:google-sso-202308.description', options),
    },
  },
}

const getProcessingTranslationDefaultValue = (options?: ProcessingAssetTranslationOptions): string => {
  if (typeof options?.defaultValue === 'string') {
    return options.defaultValue
  }

  return ''
}

const getProcessingTranslationResolver = (
  config: ProcessingAssetTranslationConfig | undefined,
  variant: ExperimentValue,
  flow: PostCheckoutFlow
): ProcessingAssetTranslationResolver | undefined => {
  if (!config) {
    return undefined
  }

  const flowVariantResolver = config.flowVariantResolvers?.[`${variant}-${flow}`]
  if (flowVariantResolver !== undefined) {
    return flowVariantResolver
  }

  const variantResolver = config.variantResolvers?.[variant]
  if (variantResolver !== undefined) {
    return variantResolver
  }

  if (config.baseResolver !== undefined) {
    return config.baseResolver
  }

  return undefined
}

interface GetProcessingAssetTranslationParams {
  id: AssetId
  variant: ExperimentValue
  flow: PostCheckoutFlow
  type: ProcessingTextType
  t: TFunction
  options?: ProcessingAssetTranslationOptions
}

export const getProcessingAssetTranslation = ({ id, variant, flow, type, t, options }: GetProcessingAssetTranslationParams): string => {
  const assetTranslationMap = PROCESSING_ASSET_TRANSLATION_MAP[id]
  const translationConfig = assetTranslationMap?.[type]
  const translationResolver = getProcessingTranslationResolver(translationConfig, variant, flow)

  if (translationResolver === undefined) {
    return getProcessingTranslationDefaultValue(options)
  }

  return translationResolver(t, options)
}

export const getContractAgreementLoadingMessages = (t: TranslateFn, key: LoggedInLoadingMessageKey): LoadingMessages | null => {
  switch (key) {
    case 'unsuitabilityQuestions':
      return {
        title: t('unsuitabilityQuestions:title'),
        description: t('unsuitabilityQuestions:description'),
      }
    case 'verifyIdentity':
      return {
        title: t('verifyIdentity:title'),
        description: t('verifyIdentity:description'),
      }
    default:
      return null
  }
}

interface GetPaymentMethodCardErrorMessageParams {
  cardError: CardErrorReason | null | undefined
  cardBrand?: CardBrand | ''
  cardExpiryLimitInMonths?: number
  isInline?: boolean
  t: TFunction
}

export const getPaymentMethodCardErrorMessage = ({
  cardError,
  cardBrand,
  cardExpiryLimitInMonths = PCL_CARD_EXPIRY_LIMIT_IN_MONTHS,
  isInline = false,
  t,
}: GetPaymentMethodCardErrorMessageParams): string => {
  if (cardBrand === CardBrand.AMEX && cardError === 'invalidBrand') {
    return isInline ? t('paymentMethod:errors.amexUnsupported_inline') : t('paymentMethod:errors.amexUnsupported')
  }

  switch (cardError) {
    case 'invalidBrand':
      return t('paymentMethod:errors.invalidBrand', { selectedCardBrand: cardBrand })
    case 'cardExpiry':
      return t('paymentMethod:errors.cardExpiry')
    case 'achNotAllowed':
      return t('paymentMethod:errors.achNotAllowed')
    case 'creditCardNotAllowed':
      return t('paymentMethod:errors.creditCardNotAllowed')
    case 'cardExpiryLimitInMonths':
      return t('paymentMethod:errors.cardExpiryLimitInMonths', { count: cardExpiryLimitInMonths })
    case 'cashAppCardIneligible':
      return t('paymentMethod:errors.cashAppCardIneligible')
    case 'dssProhibitedPaymentMethod':
      return t('paymentMethod:errors.dssProhibitedPaymentMethod')
    case 'unacceptableCard':
      return t('paymentMethod:errors.unacceptableCard', { selectedCardBrand: cardBrand })
    case 'unacceptableIssuerRestrictedCard':
      return t('paymentMethod:errors.unacceptableIssuerRestrictedCard', { selectedCardBrand: cardBrand })
    case 'fallbackGenericMessage':
    default:
      return t('paymentMethod:errors.fallbackGenericMessage')
  }
}
